/**
 * Guards against internal AI protocol markers reaching a person.
 *
 * The WhatsApp path asks RAG to answer normally OR emit `<<ACTION_REQUIRED>>`
 * to request a second, tool-enabled pass (see backend
 * ai-tools/capability-preamble.util.ts). Webchat and the playground never opt
 * into that protocol — they don't send the rule and have no tool pass — so the
 * marker should be impossible there.
 *
 * It happened anyway: a widget visitor asking "can you send me baatly logo?"
 * was shown the literal string `<<ACTION_REQUIRED>>`. The backend route by
 * which it escaped is still being traced, which is exactly why this guard
 * exists at the render layer: a raw protocol marker must be unable to reach a
 * customer regardless of how it got into the text.
 *
 * Deliberately applied at RENDER, not at accumulation — the playground streams
 * tokens, so the same text is re-sanitised on every frame and a half-arrived
 * marker never flashes on screen.
 */

/** Must stay in sync with the backend's ACTION_REQUIRED_SENTINEL. */
export const ACTION_REQUIRED_SENTINEL = '<<ACTION_REQUIRED>>';

/**
 * Shown when the marker was the whole reply.
 *
 * Deliberately NOT "we don't have that capability" — that would often be false.
 * The tenant may well have the file; it's this CHANNEL that has no way to send
 * it, and the same request on WhatsApp succeeds. Claiming the capability
 * doesn't exist is a lie the tenant would object to, so this says what is
 * actually true: not from here, and a human can.
 */
export const ACTION_UNAVAILABLE_FALLBACK =
  "I'm not able to do that from this chat — someone from our team can help with it. Is there anything else I can answer?";

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * A leading slice of the sentinel sitting at the very END of the text — i.e. a
 * marker still arriving token by token.
 *
 * Two details that were BOTH wrong in the first version and silently corrupted
 * ordinary replies, so don't "simplify" them away:
 *
 *  1. The alternation is wrapped in `(?:…)`. Without the group, `a|b|c$` anchors
 *     only the LAST alternative, so short prefixes like `<<` matched anywhere in
 *     the string — "Compare a << b" rendered as "Compare a  b".
 *  2. Each prefix is escaped AFTER slicing, not sliced out of a pre-escaped
 *     string. Escaping first then splitting per character tears `\.` in half the
 *     moment the sentinel contains a regex metacharacter.
 *
 * Longest-first so the fullest partial wins.
 */
const PARTIAL_SENTINEL_RE = new RegExp(
  `(?:${ACTION_REQUIRED_SENTINEL.split('')
    .map((_, i, arr) => escapeRegExp(arr.slice(0, i + 1).join('')))
    .reverse()
    .join('|')})$`
);

/**
 * Render-safe assistant text.
 *
 * - Full marker present → removed. If nothing else remains, the fallback is
 *   returned rather than an empty bubble.
 * - Marker mixed with prose → prose kept, marker dropped (the model was told
 *   never to mix them, but a guard that trusts the model isn't a guard).
 * - Marker still streaming in → the dangling prefix is hidden until it
 *   resolves, so `<<ACTION_REQ` never appears mid-stream.
 */
export function sanitizeAssistantText(raw: string): string {
  if (!raw) return raw;

  if (raw.includes(ACTION_REQUIRED_SENTINEL)) {
    const remainder = raw.split(ACTION_REQUIRED_SENTINEL).join('').replace(/\n{3,}/g, '\n\n').trim();
    return remainder || ACTION_UNAVAILABLE_FALLBACK;
  }

  // Mid-stream: strip a trailing partial marker without touching real text.
  return raw.replace(PARTIAL_SENTINEL_RE, '');
}
